// RestoreDatabase — Admin-only DB restore widget (server backups + file upload).
// 2026-05-29 EXTRACTED verbatim from src/App.jsx (C1 monolith split, Phase 2d3b2).
// Behavior-preserving move: body identical to the in-App.jsx version; only imports
// were added (it previously closed over App.jsx-scope symbols). Rendered by AdminPanel.
import { useState } from 'react';
import { API_BASE } from '../lib/endpoints.js';
import { authHeaders } from '../lib/auth-headers.js';
import { LS_KEYS, LS_VERSIONS_KEY, LS_DELETED_IDS_KEY, LS_TOMBSTONED_IDS_KEY } from '../lib/local-storage.js';

function RestoreDatabase({ user }) {
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState(null);

  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/backups`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBackups((Array.isArray(data) ? data : data.backups || []).slice(0, 3));
      }
    } catch (e) { console.error("Failed to fetch backups:", e); }
    setBackupsLoading(false);
  };

  const handleRestore = async (backupName) => {
    if (!confirm(`⚠️ RESTORE DATABASE\n\nThis will replace ALL current data with the backup:\n${backupName}\n\nThis cannot be undone. A safety backup of current data will be created first.\n\nAre you sure?`)) return;
    if (!confirm(`FINAL CONFIRMATION\n\nRestore from: ${backupName}\n\nAll current data will be overwritten. Continue?`)) return;
    setRestoreStatus(`Restoring ${backupName}...`);
    try {
      const res = await fetch(`${API_BASE}/restore/${encodeURIComponent(backupName)}`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ user: user?.email || "admin" })
      });
      const data = await res.json();
      if (data.ok) {
        // R2-4 (жюри A, PR R fixes round 2, 28.09) — backup.cjs's restore now skips a
        // corrupt table instead of aborting (F5) and reports it in `skipped`, but this
        // screen only ever read `data.ok` — an admin restoring after a bad backup saw
        // a plain "Restored", with no sign that one table (e.g. a corrupt registry)
        // was NOT actually restored. Surface it in the same status line.
        const skipped = Array.isArray(data.skipped) ? data.skipped : [];
        const skippedNote = skipped.length > 0 ? ` Skipped (corrupt in backup, not restored): ${skipped.join(', ')}.` : '';
        setRestoreStatus(`✅ Restored from ${backupName}! Reloading...${skippedNote}`);
        // FIX: clear notification cache so restore doesn't trigger Telegram flood
        try { await fetch(`${API_BASE}/admin/clear-notif-cache`, { method:'POST', headers: authHeaders() }); } catch {}
        Object.values(LS_KEYS).forEach(k => { try { localStorage.removeItem(k); } catch {} });
        try { localStorage.removeItem(LS_VERSIONS_KEY); } catch {}
        // 2026-06-04 (audit F2): a restore replaces ALL data with the backup — also clear the
        // durable delete/tombstone trackers, else a previously-deleted record present in the
        // backup stays hidden by a stale tombstone after the reload below.
        try { localStorage.removeItem(LS_DELETED_IDS_KEY); localStorage.removeItem(LS_TOMBSTONED_IDS_KEY); } catch {}
        // FIX 15: Explicitly close WS before reload — iOS PWA doesn't reconnect if WS is orphaned
        setTimeout(() => { try { if(window._blitzWS) window._blitzWS.close(); } catch{} window.location.reload(); }, 1500);
      } else {
        setRestoreStatus(`❌ Restore failed: ${data.error || "Unknown error"}`);
      }
    } catch (e) { setRestoreStatus(`❌ Restore failed: ${e.message}`); }
  };

  const handleFileRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const tables = ['users', 'payments', 'customer-payments', 'crg-deals', 'daily-cap', 'deals', 'wallets', 'offers', 'ftd-entries'];
      const found = tables.filter(t => data[t] && Array.isArray(data[t]));
      if (found.length === 0) { alert("❌ Invalid backup file — no data tables found."); return; }
      const summary = found.map(t => `  ${t}: ${data[t].length} records`).join("\n");
      if (!confirm(`📦 Backup file: ${file.name}\n\nContains:\n${summary}\n\n⚠️ This will OVERWRITE all current data.\n\nContinue?`)) return;
      if (!confirm("FINAL CONFIRMATION — Restore now? This cannot be undone.")) return;
      setRestoreStatus("Uploading and restoring...");
      try { await fetch(`${API_BASE}/admin/backup`, { method: "POST", headers: authHeaders() }); } catch {}
      let ok = 0;
      for (const t of found) {
        try {
          const res = await fetch(`${API_BASE}/${t}`, {
            method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify({ data: data[t], version: 0, user: user?.email || "restore", forceOverwrite: true })
          });
          if (res.ok) ok++;
        } catch {}
      }
      if (ok > 0) {
        setRestoreStatus(`✅ Restored ${ok}/${found.length} tables from ${file.name}! Reloading...`);
        // FIX: clear notification cache so restore doesn't trigger Telegram flood
        try { await fetch(`${API_BASE}/admin/clear-notif-cache`, { method:'POST', headers: authHeaders() }); } catch {}
        Object.values(LS_KEYS).forEach(k => { try { localStorage.removeItem(k); } catch {} });
        try { localStorage.removeItem(LS_VERSIONS_KEY); } catch {}
        // 2026-06-04 (audit F2): a restore replaces ALL data with the backup — also clear the
        // durable delete/tombstone trackers, else a previously-deleted record present in the
        // backup stays hidden by a stale tombstone after the reload below.
        try { localStorage.removeItem(LS_DELETED_IDS_KEY); localStorage.removeItem(LS_TOMBSTONED_IDS_KEY); } catch {}
        setTimeout(() => { try { if(window._blitzWS) window._blitzWS.close(); } catch{} window.location.reload(); }, 1500);
      } else {
        setRestoreStatus("❌ Restore failed — no tables were saved successfully.");
      }
    } catch (err) { setRestoreStatus("❌ Invalid file: " + err.message); }
  };

  return (
    <div style={{ marginTop: 32, padding: "20px 24px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 14 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#92400E", marginBottom: 16 }}>🔄 Restore Database</div>

      {/* Server backups */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#92400E" }}>Server Backups (last 3)</span>
          <button onClick={fetchBackups} style={{ padding: "6px 14px", borderRadius: 8, background: "#F59E0B", border: "none", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
            {backupsLoading ? "Loading..." : "↻ Load Backups"}
          </button>
        </div>
        {backups.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {backups.map((b, i) => (
              <div key={b.name || i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#FFF", border: "1px solid #FDE68A", borderRadius: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", fontFamily: "'JetBrains Mono',monospace" }}>{b.name}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>
                    {b.files ? `${b.files} files` : ""}{b.date ? ` • ${new Date(b.date).toLocaleString()}` : b.timestamp ? ` • ${b.timestamp}` : ""}
                  </div>
                </div>
                <button onClick={() => handleRestore(b.name)}
                  style={{ padding: "6px 14px", borderRadius: 8, background: "linear-gradient(135deg,#EF4444,#F87171)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                  ⏪ Restore
                </button>
              </div>
            ))}
          </div>
        )}
        {backups.length === 0 && !backupsLoading && (
          <div style={{ fontSize: 12, color: "#B45309", padding: "10px 14px", background: "#FFF", borderRadius: 8, border: "1px dashed #FDE68A" }}>Click "Load Backups" to fetch server backups, or use file upload below.</div>
        )}
      </div>

      {/* Divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "14px 0" }}>
        <div style={{ flex: 1, height: 1, background: "#FDE68A" }} />
        <span style={{ fontSize: 12, color: "#B45309", fontWeight: 600 }}>OR</span>
        <div style={{ flex: 1, height: 1, background: "#FDE68A" }} />
      </div>

      {/* File upload restore */}
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#92400E", display: "block", marginBottom: 8 }}>Restore from Backup File</span>
        <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 16px", background: "#FFF", border: "2px dashed #FDE68A", borderRadius: 10, cursor: "pointer", transition: "all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#F59E0B"; e.currentTarget.style.background = "#FFFEF5"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "#FDE68A"; e.currentTarget.style.background = "#FFF"; }}
        >
          <span style={{ fontSize: 22 }}>📂</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#92400E" }}>Choose backup JSON file...</span>
          <input type="file" accept=".json" style={{ display: "none" }} onChange={handleFileRestore} />
        </label>
      </div>

      {restoreStatus && (
        <div style={{ marginTop: 10, fontSize: 13, color: restoreStatus.startsWith("✅") ? "#16A34A" : restoreStatus.startsWith("❌") ? "#DC2626" : "#92400E", padding: "10px 14px", background: "#FFF", borderRadius: 8, fontWeight: 500 }}>
          {restoreStatus}
        </div>
      )}
      <div style={{ fontSize: 11, color: "#B45309", marginTop: 10 }}>⚠️ Restoring will overwrite ALL current data. A safety backup is created automatically first.</div>
    </div>
  );
}

export { RestoreDatabase };
export default RestoreDatabase;
